// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Eintragen (<10-Sekunden-Flow), portiert aus docs/referenz/trainingslog.html #tab-log
import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import type { Exercise, Feel, PlanDay, PlanDayItem, SetEntry, WorkoutEntry } from "@/lib/types"
import {
  getAllEntries, getPlans, getPlanDays, getPlanDayItems, getExercises,
  saveEntry, updateEntry, onSynced, sofort, nachId, planTage,
} from "@/lib/data"
import { cleanSets, DEFAULT_SETS, fmtWeight, isBodyweight, repsSpalte } from "@/lib/sets"
import { todayISO, dayOf } from "@/lib/dates"
import { milestoneCrossed, isDayComplete } from "@/lib/stats"
import { checkRecord } from "@/lib/records"
import { prefillSets } from "@/lib/prefill"
import { celebrate } from "@/lib/celebrate"
import { toast } from "@/lib/toast"
import DayToggle from "@/components/log/DayToggle"
import ExerciseChips from "@/components/log/ExerciseChips"
import SetList from "@/components/log/SetList"
import FeelPicker from "@/components/log/FeelPicker"
import ExercisePeek from "@/components/log/ExercisePeek"
import ExercisePicker from "@/components/plans/ExercisePicker"
import Wichtel from "@/components/celebration/creatures/Wichtel"
import "@/styles/log.css"
import "@/styles/planedit.css" // Picker-Styles (Katalog-Suche)

export default function LogPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const editEntry = (location.state as { editEntry?: WorkoutEntry } | null)?.editEntry ?? null

  // Sofort-Speicher: beim Tab-Wechsel steht die Seite schon (Runde 16)
  const [loaded, setLoaded] = useState(() => sofort.plans() !== null)
  const [hasPlan, setHasPlan] = useState(true)
  const [days, setDays] = useState<PlanDay[]>(() => planTage(sofort.plans(), sofort.planDays()))
  const [items, setItems] = useState<PlanDayItem[]>(() => sofort.planDayItems() ?? [])
  const [exercises, setExercises] = useState<Map<string, Exercise>>(() => nachId(sofort.exercises()))
  const [entries, setEntries] = useState<WorkoutEntry[]>(() => sofort.entries() ?? [])

  const [date, setDate] = useState(todayISO())
  const [dayId, setDayId] = useState("")
  const [exerciseId, setExerciseId] = useState<string | null>(null)
  const [sets, setSets] = useState<SetEntry[]>(DEFAULT_SETS.map((s) => ({ ...s })))
  const [feel, setFeel] = useState<Feel>("gut")
  const [note, setNote] = useState("")
  const [saving, setSaving] = useState(false)
  const [ok, setOk] = useState(false) // Erfolgs-Morph-Phase (Skill §10)
  const [suche, setSuche] = useState(false) // Katalog-Suche für Übungen außerhalb des Plans

  function morph() {
    setOk(true)
    window.setTimeout(() => setOk(false), 900)
  }

  useEffect(() => {
    let alive = true
    const load = async () => {
      const [plans, allDays, allItems, exs, ents] = await Promise.all([
        getPlans(), getPlanDays(), getPlanDayItems(), getExercises(), getAllEntries(),
      ])
      if (!alive) return
      const planDays = planTage(plans, allDays)
      setHasPlan(plans.length > 0)
      setDays(planDays)
      // gewählten Tag beim Nachladen behalten
      setDayId((cur) => (cur && planDays.some((d) => d.id === cur) ? cur : planDays[0]?.id ?? ""))
      setItems(allItems)
      setExercises(nachId(exs))
      setEntries(ents)
      setLoaded(true)
    }
    void load()
    const off = onSynced(() => { void load() })
    return () => { alive = false; off() }
  }, [])

  // Edit-Modus: Formular aus dem Eintrag vorbelegen, sobald die Plan-Tage da sind
  useEffect(() => {
    if (!editEntry || !days.length) return
    const day = days.find((d) => d.id === editEntry.plan_day) ?? days.find((d) => d.code === editEntry.day_code)
    setDate(dayOf(editEntry.date))
    if (day) setDayId(day.id)
    setExerciseId(editEntry.exercise)
    setSets(editEntry.sets.map((s) => ({ ...s })))
    setFeel(editEntry.feel)
    setNote(editEntry.note)
  }, [editEntry, days])

  const dayItems = useMemo(
    () => items.filter((i) => i.plan_day === dayId).sort((a, b) => a.sort - b.sort),
    [items, dayId],
  )
  const selectedExercise = exerciseId ? exercises.get(exerciseId) : undefined

  // Schnellzugriff: die 3 zuletzt geloggten Übungen — Wiederholtäter zuerst
  const zuletzt = useMemo(() => {
    const seen = new Set<string>()
    const out: Exercise[] = []
    for (const e of [...entries].sort((a, b) => dayOf(b.date).localeCompare(dayOf(a.date)))) {
      if (seen.has(e.exercise)) continue
      seen.add(e.exercise)
      const ex = exercises.get(e.exercise)
      if (ex) out.push(ex)
      if (out.length === 3) break
    }
    return out
  }, [entries, exercises])

  function selectDay(id: string) {
    setDayId(id)
    setExerciseId(null)
  }

  function selectExercise(id: string) {
    setExerciseId(id)
    const target = dayItems.find((i) => i.exercise === id)?.target_sets ?? 0
    const { sets: pre, prefilled } = prefillSets(entries, id, target, isBodyweight(exercises.get(id)?.equipment))
    setSets(pre)
    if (prefilled) toast("Letzte Werte übernommen – anpassen & speichern")
  }

  async function save() {
    if (saving) return
    if (!exerciseId) { toast("Bitte zuerst eine Übung wählen"); return }
    const clean = cleanSets(sets)
    if (!clean.length) { toast("Mindestens ein Satz mit Wiederholungen"); return }
    // Übungen außerhalb des Plans hängen an keinem Trainingstag (Katalog-Suche)
    const effDayId = dayItems.some((i) => i.exercise === exerciseId)
      ? dayId
      : items.find((i) => i.exercise === exerciseId && days.some((d) => d.id === i.plan_day))?.plan_day ?? ""
    const dayCode = days.find((d) => d.id === effDayId)?.code ?? ""
    const saveDate = date || todayISO()
    const payload = {
      date: saveDate, plan_day: effDayId, day_code: dayCode,
      exercise: exerciseId, feel, note: note.trim(), sets: clean,
    }

    setSaving(true)
    try {
      if (editEntry) {
        await updateEntry(editEntry, payload)
        morph()
        setEntries(await getAllEntries())
        navigate("/log", { replace: true, state: null })
        toast("Änderung gespeichert ✓")
        return
      }

      const entriesBefore = entries
      const saved = await saveEntry(payload)
      morph()
      // Rekord schlägt die schlichte Speicher-Feier (record > save); Meilensteine laufen weiter.
      const rec = checkRecord(entriesBefore, saved)
      if (rec) {
        const recText = `Neuer Rekord: ${selectedExercise?.name ?? "Übung"} ${fmtWeight(rec.value)} kg!`
        celebrate({ kind: "record", text: recText })
        toast(recText)
      } else {
        toast(`Gespeichert: ${selectedExercise?.name ?? "Übung"} ✓`)
        celebrate({ kind: "save" })
      }
      const ms = milestoneCrossed(entriesBefore, saveDate)
      if (ms) {
        celebrate({ kind: "milestone", text: ms })
        toast(ms)
      }
      const updated = await getAllEntries()
      setEntries(updated)
      if (isDayComplete(dayItems, updated, saveDate)) {
        celebrate({ kind: "day-complete", dayCode })
        toast(`Alle Übungen von Tag ${dayCode} geschafft – stark und stabil!`)
      }
      setNote("")
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  if (!hasPlan) {
    return (
      <div className="empty">
        <div className="empty-critter"><Wichtel /></div>
        <p><b>Kein Plan? Bau dir einen.</b></p>
        <button type="button" className="btn-secondary" onClick={() => navigate("/plaene")}>Plan anlegen</button>
      </div>
    )
  }

  return (
    <>
      {/* Aufgabe der Seite in einem Satz — sie ist der Notizblock neben dem Training */}
      <p className="hint page-zweck">
        Für einzelne Übungen – auch nachträglich. Im Studio führt dich der Tab „Training".
      </p>

      {/* Übung zuerst: die eigentliche Frage der Seite (Runde 4) */}
      <h2>Übung</h2>
      {zuletzt.length > 0 && !editEntry && (
        <>
          <p className="sublabel">Zuletzt geloggt</p>
          <div className="chips">
            {zuletzt.map((ex) => (
              <button
                key={ex.id}
                type="button"
                aria-pressed={ex.id === exerciseId}
                onClick={() => selectExercise(ex.id)}
              >{ex.name}</button>
            ))}
          </div>
        </>
      )}

      <p className="sublabel">Aus deinem Plan</p>
      <DayToggle days={days} selected={dayId} onSelect={selectDay} />
      <ExerciseChips items={dayItems} exercises={exercises} selected={exerciseId} onSelect={selectExercise} />

      <button type="button" className="peek-toggle" onClick={() => setSuche((s) => !s)}>
        Andere Übung suchen {suche ? "▾" : "▸"}
      </button>
      {suche && (
        <div className="card">
          <ExercisePicker
            exercises={[...exercises.values()]}
            onPick={(ex) => { selectExercise(ex.id); setSuche(false) }}
          />
        </div>
      )}

      {selectedExercise && <ExercisePeek key={selectedExercise.id} exercise={selectedExercise} />}

      <h2>Sätze{selectedExercise ? ` · ${selectedExercise.name}` : ""}</h2>
      <div className="card">
        <SetList key={exerciseId ?? "leer"} sets={sets} onChange={setSets} hideWeight={isBodyweight(selectedExercise?.equipment)} repsLabel={repsSpalte(selectedExercise?.default_reps)} />
      </div>

      <h2>Gefühl</h2>
      <FeelPicker value={feel} onChange={setFeel} />
      <p className="hint">Bei Ausstrahlung, Kribbeln oder Taubheit ins Bein: Training abbrechen und ärztlich abklären.</p>

      {/* Notiz + Datum zusammengelegt — weniger schreiende Abschnitte (Runde 5) */}
      <h2>Details</h2>
      <textarea
        rows={2}
        aria-label="Notiz"
        placeholder="Notiz, z. B. Sitz auf Position 4, nächstes Mal 2,5 kg mehr"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <input type="date" aria-label="Trainingsdatum" value={date} onChange={(e) => setDate(e.target.value)} style={{ marginTop: 8 }} />

      <button className={`save save-sticky btn-success-morph${ok ? " ok" : ""}`} type="button" disabled={saving || ok} onClick={() => void save()}>
        <span className="bsm-label">{saving ? "Speichert…" : editEntry ? "Änderung speichern" : "Übung speichern"}</span>
        <svg className="bsm-check" viewBox="0 0 24 24" aria-hidden="true">
          <path pathLength="1" d="M4.5 12.5 10 18 19.5 6.5" />
        </svg>
      </button>
    </>
  )
}
